import React, { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Select, Input, Modal } from 'common/components';
import {
    getWorkflowsWithOwnSip,
    reduceWorkflowNumbers,
    ReduceWorkflowNumbersResponse,
    WorkflowWithOwnSip,
} from 'services/api/PhoneNumberAPI';
import { Tenant } from 'types/accessManager.type';
import sharedStyles from '../PhoneNumberManager.module.scss';
import styles from './ReduceWorkflowNumbersTab.module.scss';

interface ReduceForm {
    tenantId: string;
    workflowId: string;
    workflowIdOverride: string;
    keepNumbers: number;
    includeOrphans: boolean;
}

interface ReduceWorkflowNumbersTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const ReduceWorkflowNumbersTab: React.FC<ReduceWorkflowNumbersTabProps> = ({
    tenants,
    isAccessManagerLoading,
    showNotification,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [dedicatedSipWorkflows, setDedicatedSipWorkflows] = useState<WorkflowWithOwnSip[]>([]);
    const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [result, setResult] = useState<ReduceWorkflowNumbersResponse | null>(null);
    const latestTenantFetchRef = useRef<string>('');

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<ReduceForm>({
        defaultValues: {
            tenantId: '',
            workflowId: '',
            workflowIdOverride: '',
            keepNumbers: 1,
            includeOrphans: true,
        },
    });

    const selectedTenantId = watch('tenantId');
    const selectedWorkflowId = watch('workflowId');
    const workflowIdOverride = watch('workflowIdOverride');
    const keepNumbers = watch('keepNumbers');
    const includeOrphans = watch('includeOrphans');

    const dedicatedSipWorkflowOptions = useMemo(
        () =>
            dedicatedSipWorkflows.map((w) => ({
                id: w.workflowId,
                title: w.workflowTitle || w.workflowId,
            })),
        [dedicatedSipWorkflows]
    );

    const dedicatedSipIdSet = useMemo(
        () => new Set(dedicatedSipWorkflows.map((w) => w.workflowId)),
        [dedicatedSipWorkflows]
    );

    const handleTenantChange = (tenantId: string) => {
        setValue('tenantId', tenantId);
        setValue('workflowId', '');
        setValue('workflowIdOverride', '');
        setShowAdvanced(false);
        setResult(null);
        setDedicatedSipWorkflows([]);

        latestTenantFetchRef.current = tenantId;
        if (!tenantId) return;

        setIsLoadingWorkflows(true);
        getWorkflowsWithOwnSip(tenantId)
            .then((res) => {
                if (latestTenantFetchRef.current !== tenantId) return;
                setDedicatedSipWorkflows(res.workflows || []);
            })
            .catch((error: any) => {
                if (latestTenantFetchRef.current !== tenantId) return;
                const message =
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    error.message ||
                    'Failed to load workflows with dedicated SIP.';
                showNotification(message, 'error');
            })
            .finally(() => {
                if (latestTenantFetchRef.current !== tenantId) return;
                setIsLoadingWorkflows(false);
            });
    };

    const effectiveWorkflowId = workflowIdOverride.trim() || selectedWorkflowId;
    const overrideActive = workflowIdOverride.trim().length > 0;
    const isBaseScopeIntent = overrideActive || (!!selectedWorkflowId && !dedicatedSipIdSet.has(selectedWorkflowId));

    const onSubmit = (data: ReduceForm) => {
        const wfId = data.workflowIdOverride.trim() || data.workflowId;
        if (!data.tenantId || !wfId) return;
        setShowConfirmModal(true);
    };

    const handleConfirm = async () => {
        if (!selectedTenantId || !effectiveWorkflowId) return;
        setShowConfirmModal(false);
        setIsSubmitting(true);
        setResult(null);

        try {
            const response = await reduceWorkflowNumbers({
                tenantId: selectedTenantId,
                workflowId: effectiveWorkflowId,
                keepNumbers: Number(keepNumbers),
                includeOrphans,
            });
            setResult(response);

            const isNoop = response.before === response.after;
            const hasWarnings = !!response.warnings && response.warnings.length > 0;
            showNotification(
                response.message || `Reduced from ${response.before} to ${response.after} numbers.`,
                isNoop || hasWarnings ? 'error' : 'success'
            );
        } catch (error: any) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to reduce workflow numbers. Please try again.';
            showNotification(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        reset();
        setResult(null);
        setShowAdvanced(false);
        setDedicatedSipWorkflows([]);
    };

    const submitDisabled =
        !selectedTenantId ||
        !effectiveWorkflowId ||
        keepNumbers === undefined ||
        keepNumbers === null ||
        Number(keepNumbers) < 0 ||
        isSubmitting ||
        isAccessManagerLoading;

    return (
        <div className={sharedStyles.createTrunkContainer}>
            <form onSubmit={handleSubmit(onSubmit)} className={sharedStyles.form}>
                <div className={sharedStyles.formSection}>
                    <h2 className={sharedStyles.sectionTitle}>Reduce Workflow Numbers</h2>
                    <p className={sharedStyles.description}>
                        Release excess Twilio phone numbers from a workflow's SIP trunk. Deletions propagate to Twilio,
                        the <code>twilio_numbers</code> table, and LiveKit (outbound + optional inbound trunk). If the
                        selected workflow has no dedicated SIP, the operation falls back to the shared{' '}
                        <strong>base trunk</strong>.
                    </p>

                    <div className={sharedStyles.formGroup}>
                        <Controller
                            name='tenantId'
                            control={control}
                            rules={{ required: 'Tenant is required' }}
                            render={({ field }) => (
                                <Select
                                    label='Tenant *'
                                    options={[
                                        { value: '', label: 'Select a Tenant', disabled: true },
                                        ...(tenants || []).map((tenant) => ({
                                            value: tenant.id,
                                            label: tenant.name,
                                        })),
                                    ]}
                                    name={field.name}
                                    ref={field.ref}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        handleTenantChange(e.target.value)
                                    }
                                    disabled={isAccessManagerLoading}
                                />
                            )}
                        />
                        {errors.tenantId && <span className={sharedStyles.errorText}>{errors.tenantId.message}</span>}
                    </div>

                    <div className={sharedStyles.formGroup}>
                        <Controller
                            name='workflowId'
                            control={control}
                            rules={{
                                validate: (value) => overrideActive || !!value || 'Workflow is required',
                            }}
                            render={({ field }) => (
                                <Select
                                    label='Workflow (dedicated SIP) *'
                                    options={[
                                        {
                                            value: '',
                                            label: isLoadingWorkflows
                                                ? 'Loading workflows...'
                                                : dedicatedSipWorkflowOptions.length === 0
                                                  ? 'No workflows with dedicated SIP'
                                                  : 'Select a Workflow',
                                            disabled: true,
                                        },
                                        ...dedicatedSipWorkflowOptions.map((w) => ({
                                            value: w.id,
                                            label: w.title,
                                        })),
                                    ]}
                                    {...field}
                                    disabled={
                                        !selectedTenantId ||
                                        isLoadingWorkflows ||
                                        overrideActive ||
                                        isAccessManagerLoading
                                    }
                                />
                            )}
                        />
                        {errors.workflowId && !overrideActive && (
                            <span className={sharedStyles.errorText}>{errors.workflowId.message}</span>
                        )}
                    </div>

                    <button type='button' className={styles.advancedToggle} onClick={() => setShowAdvanced((s) => !s)}>
                        {showAdvanced ? 'Hide advanced' : 'Advanced: force base scope by workflow ID'}
                    </button>

                    {showAdvanced && (
                        <div className={sharedStyles.formGroup}>
                            <Controller
                                name='workflowIdOverride'
                                control={control}
                                rules={{
                                    pattern: {
                                        value: /^$|^[0-9a-fA-F-]{36}$/,
                                        message: 'Must be a valid UUID',
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        label='Workflow ID (overrides dropdown)'
                                        type='text'
                                        placeholder='00000000-0000-0000-0000-000000000000'
                                        {...field}
                                    />
                                )}
                            />
                            <p className={styles.advancedHint}>
                                Entering a workflow ID here will operate on the <strong>base trunk</strong> if the
                                workflow has no dedicated SIP — affecting every base-scope workflow on the tenant.
                            </p>
                            {errors.workflowIdOverride && (
                                <span className={sharedStyles.errorText}>{errors.workflowIdOverride.message}</span>
                            )}
                        </div>
                    )}

                    <div className={sharedStyles.formGroup}>
                        <Controller
                            name='keepNumbers'
                            control={control}
                            rules={{
                                required: 'keepNumbers is required',
                                min: { value: 0, message: 'Must be 0 or greater' },
                            }}
                            render={({ field }) => (
                                <Input
                                    label='Numbers to keep *'
                                    type='number'
                                    min={0}
                                    helperText='Number of active numbers to RETAIN on the trunk after the reduction. 0 releases every number.'
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                        field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                                    }
                                />
                            )}
                        />
                        {errors.keepNumbers && (
                            <span className={sharedStyles.errorText}>{errors.keepNumbers.message}</span>
                        )}
                    </div>

                    <div className={sharedStyles.formGroup}>
                        <Controller
                            name='includeOrphans'
                            control={control}
                            render={({ field }) => (
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type='checkbox'
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                    Include orphaned numbers (delete DB rows with no matching Twilio number)
                                </label>
                            )}
                        />
                    </div>

                    <div className={sharedStyles.buttonContainer}>
                        <Button type='button' variant='secondary' onClick={handleReset} disabled={isSubmitting}>
                            Reset
                        </Button>
                        <Button type='submit' variant='primary' disabled={submitDisabled}>
                            {isSubmitting ? 'Reducing...' : 'Reduce Numbers'}
                        </Button>
                    </div>
                </div>
            </form>

            {result && (
                <div className={styles.resultPanel}>
                    <div
                        className={`${styles.resultMessage} ${
                            result.before === result.after ? styles.info : styles.success
                        }`}
                    >
                        {result.message}
                    </div>

                    <div className={styles.scopeRow}>
                        <span>Scope:</span>
                        <span
                            className={`${styles.scopeBadge} ${
                                result.scope === 'base' ? styles.base : styles.workflow
                            }`}
                        >
                            {result.scope}
                        </span>
                        {result.scope === 'base' && (
                            <span>
                                <strong>Operated on the shared base trunk.</strong> This affected every workflow on this
                                tenant that falls back to base.
                            </span>
                        )}
                    </div>

                    <div className={styles.counts}>
                        <strong>{result.before}</strong> → <strong>{result.after}</strong> active numbers
                        {result.softDeletedRows > 0 && <> · soft-deleted {result.softDeletedRows} DB row(s)</>}
                    </div>

                    {!!result.warnings && (
                        <div className={styles.warningBanner}>
                            <strong>Warnings: </strong>
                            {result.warnings}
                            {result.livekitOutboundSynced === false && (
                                <> Verify the LiveKit outbound trunk manually — its number list may be out of sync.</>
                            )}
                        </div>
                    )}

                    {result.livekitOutboundSynced === false && !result.warnings && (
                        <div className={styles.warningBanner}>
                            <strong>LiveKit outbound not synced.</strong> Twilio and DB succeeded but LiveKit didn't —
                            verify the outbound trunk manually.
                        </div>
                    )}

                    {result.releasedNumbers.length > 0 ? (
                        <div className={styles.releasedList}>
                            <h3>Released numbers ({result.releasedNumbers.length})</h3>
                            <div className={styles.numberChips}>
                                {result.releasedNumbers.map((num) => (
                                    <span key={num} className={styles.numberChip}>
                                        {num}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : result.softDeletedRows > 0 ? (
                        <p className={styles.staleNote}>
                            No Twilio numbers needed releasing; cleaned up {result.softDeletedRows} stale DB row(s).
                        </p>
                    ) : null}
                </div>
            )}

            <Modal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                size='sm'
                header={isBaseScopeIntent ? '⚠️ Reduce numbers on shared base trunk' : 'Reduce workflow numbers'}
                showDefaultFooter={false}
                footer={
                    <div className={sharedStyles.modalFooter}>
                        <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
                            Cancel
                        </Button>
                        <Button variant='primary' onClick={handleConfirm} disabled={isSubmitting}>
                            {isBaseScopeIntent ? 'Reduce on base trunk' : 'Reduce Numbers'}
                        </Button>
                    </div>
                }
            >
                {isBaseScopeIntent ? (
                    <div className={styles.dangerModalBody}>
                        <p className={sharedStyles.modalMessage}>
                            This workflow uses the <strong>shared base trunk</strong>. Reducing will affect{' '}
                            <strong>every workflow on this tenant that uses base</strong>.
                        </p>
                        <p className={sharedStyles.modalMessage}>
                            Reduce to <strong>{Number(keepNumbers)}</strong> number
                            {Number(keepNumbers) === 1 ? '' : 's'} on the base trunk?
                        </p>
                    </div>
                ) : (
                    <p className={sharedStyles.modalMessage}>
                        Reduce to <strong>{Number(keepNumbers)}</strong> number
                        {Number(keepNumbers) === 1 ? '' : 's'} on this workflow's SIP trunk?
                    </p>
                )}
            </Modal>
        </div>
    );
};

export default ReduceWorkflowNumbersTab;
